import React, { useState, useEffect, useRef, useCallback } from "react";
import { X, Send, Sparkles } from "lucide-react";
import { getAPIRoot } from "../../services/api/core";

const parseInlineStyles = (text) => {
  if (!text) return "";
  const parts = text.split("**");
  return parts.map((part, i) => {
    if (i % 2 === 1) {
      return (
        <strong key={i} className="font-bold text-white">
          {part}
        </strong>
      );
    }
    return part;
  });
};

export default function AskStagsterSheet({ isOpen, onClose }) {
  const [messages, setMessages] = useState([
    {
      id: "welcome",
      sender: "bot",
      text: "### Hello! Welcome to the STAGSTER Concierge.\nI am here to help you navigate the app's features. Ask me about any of these real-time mechanics:\n\n* ☕ **Work Cafés**: Found under /work. Features verified plugpoints and Wi-Fi. Check-in requires being within a 280m geo-radius.\n* 🚪 **Stag Entry**: Browse venues under /stag-venues or /discover. Members check-in on-site to secure entry and share outcomes.\n* 🛡️ **Women Safety**: Vetted pubs under the /safety hub offering designated judgment-free seating zones.\n* 📡 **Radar & Matches**: Real-time active member locations under /radar. Active check-ins are visible for 6 hours. View matches on Tonight's feed under /matches.\n* 🚦 **Plan B & Crowd**: Live crowd status (Packed, Chill, Entry Tough) calculated from user feed logs. Get instant alternative spots if entry is tough.\n* ⚡ **FridayStag Pass**: Gated nightly entry queue requests and VIP entry status.",
      timestamp: new Date(),
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  // Auto-scroll to bottom of messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, loading]);

  const handleSendMessage = useCallback(async (textToSend) => {
    const query = textToSend.trim();
    if (!query) return;

    // Add user message
    const userMsg = {
      id: `user-${Date.now()}`,
      sender: "user",
      text: query,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setLoading(true);

    try {
      const res = await fetch(`${getAPIRoot()}/public/ask-stagster`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: query }),
      });
      const data = await res.json();
      
      const botMsg = {
        id: `bot-${Date.now()}`,
        sender: "bot",
        text: data.response || "Something went wrong. Let me scan again...",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, botMsg]);
    } catch (_) {
      const errorMsg = {
        id: `error-${Date.now()}`,
        sender: "bot",
        text: "Scanning offline... Please check your internet connection and try asking again.",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMsg]);
    } finally {
      setLoading(false);
    }
  }, []);

  if (!isOpen) return null;

  return (
    <div className="ask-stagster-sheet fixed inset-0 z-[75] flex items-end justify-center p-0 lg:items-center lg:z-[90]">
      {/* Backdrop */}
      <div
        className="absolute inset-0 z-0 bg-black/65 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />

      {/* Main Drawer Panel */}
      <div
        className="ask-stagster-sheet__panel relative z-[1] flex w-full max-h-[min(88dvh,calc(100dvh-5.5rem-env(safe-area-inset-bottom,0px)-0.5rem))] max-w-md flex-col overflow-hidden rounded-t-3xl border border-white/[0.07] bg-[#0c0c0e] shadow-2xl max-lg:mb-[calc(5.5rem+env(safe-area-inset-bottom,0px))] lg:max-h-[80vh] lg:rounded-3xl lg:mb-0"
        role="dialog"
        aria-modal="true"
        aria-label="Ask STAGSTER search bot"
      >
        {/* Header drag handle area */}
        <div className="shrink-0 flex flex-col items-center gap-1.5 px-4 pb-2 pt-2.5 border-b border-white/[0.05]">
          <span className="h-1 w-10 shrink-0 rounded-full bg-white/20" />
          <div className="flex items-center justify-between w-full mt-1.5 px-1">
            <div className="flex items-center gap-2.5">
              <img
                src="/logo-mascot.webp"
                alt="Mascot"
                className={`h-7 w-7 object-contain transition-all duration-300 ${loading ? "filter drop-shadow-[0_0_12px_rgba(244,63,94,0.85)] animate-pulse" : "filter drop-shadow-[0_0_8px_rgba(244,63,94,0.55)]"}`}
              />
              <div className="text-left">
                <h3 className="text-xs font-bold uppercase tracking-[0.14em] text-white">
                  Ask STAGSTER
                </h3>
                <p className="text-[9px] text-[#ffcb84] font-medium tracking-wide">
                  {loading ? "Scanning signals..." : "Online Concierge"}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="flex h-7 w-7 items-center justify-center rounded-full bg-white/5 border border-white/10 hover:bg-white/10 transition text-white/60 active:scale-95"
              aria-label="Close sheet"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Messages list */}
        <div className="flex-1 min-h-0 overflow-y-auto px-4 py-4 space-y-4">
          {messages.map((msg) => {
            const isBot = msg.sender === "bot";
            return (
              <div
                key={msg.id}
                className={`flex gap-2.5 max-w-[85%] ${isBot ? "self-start" : "ml-auto flex-row-reverse"}`}
              >
                {isBot && (
                  <div className="shrink-0 flex h-7 w-7 items-center justify-center rounded-full bg-white/[0.02] border border-white/[0.05]">
                    <img src="/logo-mascot.webp" alt="Mascot" className="h-4.5 w-4.5 object-contain" />
                  </div>
                )}
                <div
                  className={`rounded-2xl px-3.5 py-2.5 text-xs leading-[1.6] ${
                    isBot
                      ? "bg-white/[0.03] border border-white/[0.05] text-white/90"
                      : "bg-gradient-to-r from-rose-500/10 to-orange-500/10 border border-rose-500/20 text-[#ffcb84]"
                  }`}
                >
                  {/* Process basic markdown lines */}
                  {msg.text.split("\n").map((line, idx) => {
                    if (line.startsWith("### ")) {
                      return (
                        <h4 key={idx} className="text-[12px] font-bold text-white mb-1.5 flex items-center gap-1.5">
                          <Sparkles className="h-3 w-3 text-rose-400" />
                          {parseInlineStyles(line.replace("### ", ""))}
                        </h4>
                      );
                    }
                    if (line.startsWith("* ")) {
                      return (
                        <div key={idx} className="pl-3.5 py-0.5 relative text-white/70">
                          <span className="absolute left-0 text-rose-500">•</span>
                          {parseInlineStyles(line.replace("* ", ""))}
                        </div>
                      );
                    }
                    return (
                      <p key={idx} className={`${line.trim() ? "mb-1.5" : "h-1.5"}`}>
                        {parseInlineStyles(line)}
                      </p>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {loading && (
            <div className="flex gap-2.5 max-w-[85%] self-start items-center">
              <div className="shrink-0 flex h-7 w-7 items-center justify-center rounded-full bg-white/[0.02] border border-[#f43f5e]/20 animate-pulse">
                <img src="/logo-mascot.webp" alt="Mascot" className="h-4.5 w-4.5 object-contain" />
              </div>
              <div className="flex gap-1.5 px-3.5 py-3 rounded-2xl bg-white/[0.02] border border-white/[0.05]">
                <span className="w-1.5 h-1.5 rounded-full bg-white/40 animate-bounce" style={{ animationDelay: "0ms" }} />
                <span className="w-1.5 h-1.5 rounded-full bg-white/40 animate-bounce" style={{ animationDelay: "150ms" }} />
                <span className="w-1.5 h-1.5 rounded-full bg-white/40 animate-bounce" style={{ animationDelay: "300ms" }} />
              </div>
            </div>
          )}
          <div ref={scrollRef} />
        </div>

        {/* Suggestion Quick Chips */}
        <div className="shrink-0 px-4 py-2 flex flex-wrap gap-2 border-t border-white/[0.04] bg-[#0c0c0e]">
          <button
            type="button"
            onClick={() => handleSendMessage("How do work café check-ins work?")}
            className="text-[10px] font-semibold text-white/60 bg-white/[0.02] border border-white/[0.06] rounded-full px-3 py-1.5 hover:bg-white/5 active:scale-95 transition"
          >
            ☕ Work Cafés
          </button>
          <button
            type="button"
            onClick={() => handleSendMessage("How is stag entry secured?")}
            className="text-[10px] font-semibold text-white/60 bg-white/[0.02] border border-white/[0.06] rounded-full px-3 py-1.5 hover:bg-white/5 active:scale-95 transition"
          >
            🚪 Stag Entry
          </button>
          <button
            type="button"
            onClick={() => handleSendMessage("What are Plan B & crowd signals?")}
            className="text-[10px] font-semibold text-white/60 bg-white/[0.02] border border-white/[0.06] rounded-full px-3 py-1.5 hover:bg-white/5 active:scale-95 transition"
          >
            🚦 Plan B / Crowd
          </button>
          <button
            type="button"
            onClick={() => handleSendMessage("How do I unlock FridayStag Pass?")}
            className="text-[10px] font-semibold text-white/60 bg-white/[0.02] border border-white/[0.06] rounded-full px-3 py-1.5 hover:bg-white/5 active:scale-95 transition"
          >
            ⚡ FridayStag Pass
          </button>
        </div>

        {/* Input Bar */}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSendMessage(input);
          }}
          className="shrink-0 p-3 border-t border-white/[0.05] bg-[#09090b] flex gap-2 items-center"
        >
          <input
            type="text"
            placeholder="Type your question..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            disabled={loading}
            className="flex-1 bg-white/[0.03] border border-white/[0.08] focus:border-rose-400 focus:bg-white/[0.05] rounded-xl px-3.5 py-2 text-xs text-white outline-none placeholder-white/30 transition-all duration-200"
          />
          <button
            type="submit"
            disabled={loading || !input.trim()}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-gradient-to-r from-rose-500 to-orange-500 text-black hover:opacity-90 active:scale-95 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Send className="h-3.5 w-3.5" />
          </button>
        </form>
      </div>
    </div>
  );
}
